import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { downloadFile } from "@/lib/upload";
import { toast } from "sonner";
import StatCard from "@/components/StatCard";
import KeaktifanBars, { toBarItems } from "@/components/KeaktifanBars";
import { StatusBadge, initials, periodRange, CAT_COLORS, greeting } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarCheck, BookOpenText, ShieldAlert, Award, Users, Inbox, FileDown, Building2, GraduationCap, LineChart as LineChartIcon, ArrowUp, ArrowDown, Minus, Info, LayoutDashboard } from "lucide-react";
import BottomNav from "@/components/BottomNav";

const PIE_COLORS = { hadir: "#059669", izin: "#F59E0B", sakit: "#3B82F6", alpa: "#EF4444" };
const CHIP_YEAR = new Date().getFullYear();
const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const MONTH_CHIPS = [{ v: "all", l: "Semua" }, ...MONTHS_ID.map((m, i) => ({ v: `${CHIP_YEAR}-${String(i + 1).padStart(2, "0")}`, l: m }))];
const TREND_CATS = [
  { key: "ngaji", label: "Ngaji Dhuha" },
  { key: "departemen", label: "Departemen" },
  { key: "ribath", label: "Ribath" },
  { key: "kuliah", label: "Kuliah" },
];

export default function WaliDashboard() {
  const { user } = useAuth();
  const { data: santri = [], isLoading } = useQuery({ queryKey: ["santri"], queryFn: async () => (await api.get("/santri")).data });
  const [activeId, setActiveId] = useState(null);
  const [period, setPeriod] = useState("all");

  useEffect(() => { if (santri.length && !activeId) setActiveId(santri[0].id); }, [santri, activeId]);

  const range = periodRange(period);
  const { data: summary } = useQuery({
    queryKey: ["summary", activeId, period],
    queryFn: async () => (await api.get(`/santri/${activeId}/summary`, { params: range })).data,
    enabled: !!activeId,
    refetchInterval: 15000,
  });

  const CUR_KEY = new Date().toISOString().slice(0, 7);
  const DAYS_ELAPSED = new Date().getDate();

  const trend = useMemo(() => {
    const tb = summary?.tren_bulanan || {};
    const keys = [...new Set(TREND_CATS.flatMap((c) => Object.keys(tb[c.key] || {})))]
      .filter((k) => /^\d{4}-\d{2}$/.test(k) && k <= CUR_KEY)
      .sort();
    return keys.map((k) => {
      const m = Number(k.slice(5, 7)) - 1;
      const row = { key: k, bulan: MONTHS_ID[m], ongoing: k === CUR_KEY };
      TREND_CATS.forEach((c) => { const v = tb[c.key]?.[k]; if (v !== undefined && v !== null) row[c.key] = v; });
      return row;
    });
  }, [summary, CUR_KEY]);

  const deltas = useMemo(() => TREND_CATS.map((c) => {
    const pts = trend.filter((r) => r[c.key] !== undefined);
    const lastRow = pts.length ? pts[pts.length - 1] : null;
    const last = lastRow ? lastRow[c.key] : null;
    const delta = pts.length >= 2 ? last - pts[pts.length - 2][c.key] : null;
    return { key: c.key, label: c.label, last, delta, ongoing: !!lastRow?.ongoing };
  }), [trend]);

  if (!isLoading && santri.length === 0) {
    return (
      <div className="glass rounded-3xl p-12 text-center max-w-lg mx-auto mt-10">
        <Inbox className="h-10 w-10 mx-auto text-emerald-400 mb-4" />
        <h2 className="text-xl font-bold text-emerald-900">Belum ada santri terhubung</h2>
        <p className="text-slate-500 dark:text-slate-300 mt-2">Akun Anda belum terhubung dengan data santri. Silakan hubungi pengurus pondok untuk menghubungkan akun Google Anda dengan putra/putri Anda.</p>
      </div>
    );
  }

  const scrollTo = (id) => { const el = document.querySelector(`[data-testid="${id}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const waliBottomNav = [
    { key: "home", label: "Beranda", icon: LayoutDashboard, active: true, onClick: () => window.scrollTo({ top: 0, behavior: "smooth" }), testId: "bottomnav-wali-home" },
    { key: "tren", label: "Tren", icon: LineChartIcon, onClick: () => scrollTo("wali-tren-bulanan"), testId: "bottomnav-wali-tren" },
    { key: "riwayat", label: "Pelanggaran", icon: ShieldAlert, onClick: () => scrollTo("table-poin-pelanggaran"), testId: "bottomnav-wali-riwayat" },
    { key: "ekspor", label: "Ekspor", icon: FileDown, onClick: () => { if (activeId) downloadFile(`/santri/${activeId}/rekap-pdf?bulan=${period}`, `rekap_${period}.pdf`); }, testId: "bottomnav-wali-ekspor" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-emerald-900 dark:text-emerald-100 tracking-tight">{greeting()}, {user?.name?.split(" ")[0] || "Wali Santri"}</h2>
          <p className="text-slate-500 dark:text-slate-300 dark:text-slate-400 dark:text-slate-500">Pilih anak untuk melihat rekap keaktifannya.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <div className="flex gap-1.5 overflow-x-auto pb-1 max-w-full sm:max-w-md" data-testid="wali-month-chips">
            {MONTH_CHIPS.map((c) => (
              <button key={c.v} onClick={() => setPeriod(c.v)} data-testid={`month-chip-${c.v}`}
                className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 transition-colors ${period === c.v ? "bg-emerald-700 text-white shadow" : "glass text-emerald-800 hover:bg-emerald-50"}`}>{c.l}</button>
            ))}
          </div>
          {activeId && (
            <>
            <Button variant="outline" className="gap-2 rounded-xl bg-white/60 shrink-0" data-testid="wali-export-button"
              onClick={() => downloadFile(`/santri/${activeId}/rekap-pdf?bulan=${period}`, `rekap_${period}.pdf`)}><FileDown className="h-4 w-4" /> Ekspor PDF</Button>
            <Button className="gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 shrink-0" data-testid="wali-surat-button"
              onClick={() => downloadFile(`/surat/rekap/${activeId}?bulan=${/^\d{4}-\d{2}$/.test(period) ? period : ""}`, `Surat_Rekap_${period}.pdf`)}><FileDown className="h-4 w-4" /> Unduh Surat Rekap Bulanan</Button>
            </>
          )}
        </div>
      </div>

      {/* Child selector */}
      <div className="flex items-center justify-center sm:justify-start gap-3 overflow-x-auto pb-1">
        <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1 shrink-0"><Users className="h-3 w-3" /> {santri.length} Anak</Badge>
        {santri.map((s) => (
          <motion.button key={s.id} whileTap={{ scale: 0.97 }} data-testid={`child-selector-button-${s.no_induk_pondok}`}
            onClick={() => setActiveId(s.id)}
            className={`flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full border transition-colors shrink-0 ${activeId === s.id ? "bg-emerald-700 border-emerald-700 text-white" : "glass border-white/60 text-emerald-900"}`}>
            <Avatar className="h-8 w-8"><AvatarImage src={s.foto} /><AvatarFallback className="text-xs bg-emerald-100 text-emerald-700">{initials(s.nama)}</AvatarFallback></Avatar>
            <div className="text-left leading-tight"><p className="text-sm font-semibold">{s.nama.split(" ")[0]}</p><p className={`text-[10px] ${activeId === s.id ? "text-emerald-200" : "text-slate-400 dark:text-slate-500"}`}>{s.departemen}</p></div>
          </motion.button>
        ))}
      </div>

      <WaConsentBanner />

      <AnimatePresence mode="wait">
        {summary && (
          <motion.div key={activeId} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-6">
            {/* Child info — hero accent card */}
            <div className="relative overflow-hidden rounded-2xl p-5 flex items-center gap-4 text-white shadow-md" style={{ background: "linear-gradient(135deg,#173404 0%,#275C0A 55%,#408310 100%)" }} data-testid="child-info-card">
              <div className="absolute -top-10 -right-8 h-32 w-32 rounded-full bg-lime-300/20 blur-2xl" />
              <Avatar className="relative z-10 h-16 w-16 ring-2 ring-white/40"><AvatarImage src={summary.santri.foto} /><AvatarFallback className="bg-emerald-900/40 text-white text-lg">{initials(summary.santri.nama)}</AvatarFallback></Avatar>
              <div className="relative z-10 flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white">{summary.santri.nama}</h3>
                <p className="text-sm text-lime-50/80">No. Induk {summary.santri.no_induk_pondok} · {summary.santri.departemen} · Ribath {summary.santri.ribath} · SMT {summary.santri.smt}</p>
                {summary.santri.nama_wali && <p className="text-sm text-lime-100 font-medium mt-0.5" data-testid="child-wali-name">Wali: {summary.santri.nama_wali}</p>}
              </div>
              <div className="relative z-10 text-right">
                <p className="text-xs text-lime-100/70">Sisa Poin</p>
                <p className="text-3xl font-extrabold text-white">{summary.sisa_poin}</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid gap-6 lg:grid-cols-5">
              <div className="lg:col-span-3"><KeaktifanBars items={toBarItems(summary)} average={summary.rata_rata_keseluruhan} title="Rekap Keaktifan Ananda" testId="wali-bars" /></div>
              <div className="lg:col-span-2 grid gap-4 grid-cols-2 lg:grid-cols-1 content-start">
                <StatCard testId="stat-card-poin-pelanggaran" icon={ShieldAlert} label="Pelanggaran" value={summary.jumlah_pelanggaran} sub={`${summary.total_poin_pelanggaran} poin`} tone="rose" delay={0.25} />
                <div className="glass glass-hover rounded-2xl p-5" data-testid="stat-card-kedisiplinan">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Nilai Kedisiplinan</p>
                      <p className="mt-2 text-3xl font-extrabold text-emerald-950 dark:text-emerald-50 leading-none">{summary.sisa_poin}<span className="text-base text-slate-400 dark:text-slate-500 font-bold">/100</span></p>
                      <p className={`mt-1.5 text-xs font-semibold ${summary.sisa_poin >= 90 ? "text-emerald-600" : summary.sisa_poin >= 70 ? "text-amber-600" : "text-rose-500"}`}>{summary.sisa_poin >= 90 ? "Baik" : summary.sisa_poin >= 70 ? "Cukup" : "Perlu Perhatian"}</p>
                    </div>
                    <div className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${summary.sisa_poin >= 90 ? "bg-emerald-100 text-emerald-700" : summary.sisa_poin >= 70 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-600"}`}><Award className="h-5 w-5" /></div>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden mt-3"><div className="h-full rounded-full" style={{ width: `${summary.sisa_poin}%`, background: summary.sisa_poin >= 90 ? "linear-gradient(90deg,#3B6D11,#639922)" : summary.sisa_poin >= 70 ? "linear-gradient(90deg,#B4820A,#D4A72C)" : "linear-gradient(90deg,#e11d48,#fb7185)" }} /></div>
                </div>
              </div>
            </div>

            {/* Rekap Tren Bulanan */}
            <div className="glass rounded-2xl p-5 sm:p-6" data-testid="wali-tren-bulanan">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 flex items-center gap-2"><LineChartIcon className="h-4 w-4 text-emerald-600" /> Rekap Tren Bulanan</h3>
                <div className="flex flex-wrap gap-3 text-xs" data-testid="wali-tren-legend">
                  {TREND_CATS.map((c) => (
                    <span key={c.key} className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CAT_COLORS[c.key].solid }} />{c.label}</span>
                  ))}
                </div>
              </div>
              {trend.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="wali-tren-deltas">
                  {deltas.map((d) => (
                    <div key={d.key} className="rounded-xl bg-white/50 dark:bg-white/5 p-3" data-testid={`wali-tren-delta-${d.key}`}>
                      <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 min-w-0"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: CAT_COLORS[d.key].solid }} /><span className="truncate">{d.label}</span></div>
                      <div className="flex items-end justify-between gap-1 mt-1">
                        <span className="text-lg font-extrabold text-emerald-900">{d.last == null || (d.ongoing && d.last === 0) ? "—" : `${d.last}%`}</span>
                        {d.ongoing ? (
                          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5">{d.last === 0 ? "belum" : "berjalan"}</span>
                        ) : d.delta == null ? <span className="text-[11px] text-slate-400 dark:text-slate-500">—</span> : (
                          <span className={`text-[11px] font-semibold flex items-center gap-0.5 ${d.delta > 0 ? "text-emerald-600" : d.delta < 0 ? "text-rose-500" : "text-slate-400 dark:text-slate-500"}`}>
                            {d.delta > 0 ? <ArrowUp className="h-3 w-3" /> : d.delta < 0 ? <ArrowDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                            {d.delta > 0 ? `+${d.delta}` : d.delta}%
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{d.ongoing ? (d.last === 0 ? "Belum ada data bulan ini" : `Baru ${DAYS_ELAPSED} hari berjalan`) : "vs bulan lalu"}</p>
                    </div>
                  ))}
                </div>
              )}
              {trend.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">Belum ada data tren bulanan.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={trend} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#D1FAE5" />
                    <XAxis dataKey="bulan" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} domain={[0, 100]} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #A7F3D0" }} formatter={(v, n) => [`${v}%`, TREND_CATS.find((c) => c.key === n)?.label || n]} />
                    {TREND_CATS.map((c) => (
                      <Line key={c.key} type="monotone" dataKey={c.key} name={c.key} stroke={CAT_COLORS[c.key].solid} strokeWidth={2.5} dot={(props) => <TrendDot {...props} color={CAT_COLORS[c.key].solid} />} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
              {trend.some((r) => r.ongoing) && (
                <p className="text-[11px] text-amber-700 mt-3 flex items-start gap-1.5" data-testid="wali-tren-ongoing-note"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />Bulan berjalan ({DAYS_ELAPSED} hari) — data belum lengkap, persentase akan bertambah seiring waktu. Titik bulan ini ditandai lingkaran kosong.</p>
              )}
            </div>

            {summary.motivasi?.pesan && (
              <div className="glass rounded-2xl p-5 border-l-4 border-emerald-500" data-testid="wali-motivasi-box">
                <p className="text-xs uppercase tracking-wide text-emerald-600 font-semibold">Catatan Motivasi · Rata-rata Keseluruhan {summary.rata_rata_keseluruhan}%</p>
                <p className="text-emerald-900 mt-1">{summary.motivasi.pesan}</p>
              </div>
            )}

            {/* Pie chart kehadiran gabungan */}
            <div className="grid gap-6 lg:grid-cols-5">
              <div className="glass rounded-2xl p-6 lg:col-span-2 flex flex-col items-center" data-testid="wali-pie-chart">
                <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-2 self-start">Kehadiran Keseluruhan</h3>
                <div className="relative" style={{ filter: "drop-shadow(0 10px 14px rgba(5,150,105,0.28))" }}>
                  <ResponsiveContainer width={220} height={220}>
                    <PieChart>
                      <defs>
                        <linearGradient id="gHadir" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34D399" /><stop offset="100%" stopColor="#047857" /></linearGradient>
                      </defs>
                      <Pie data={[
                        { name: "Hadir", key: "hadir", value: summary.gabungan.hadir },
                        { name: "Izin", key: "izin", value: summary.gabungan.izin },
                        { name: "Sakit", key: "sakit", value: summary.gabungan.sakit },
                        { name: "Alpa", key: "alpa", value: summary.gabungan.alpa },
                      ].filter((d) => d.value > 0)} dataKey="value" nameKey="name" innerRadius={62} outerRadius={95} paddingAngle={3} startAngle={90} endAngle={-270}>
                        {["hadir", "izin", "sakit", "alpa"].map((k) => <Cell key={k} fill={k === "hadir" ? "url(#gHadir)" : PIE_COLORS[k]} stroke="#fff" strokeWidth={2} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #A7F3D0" }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-3xl font-extrabold text-emerald-700" data-testid="wali-pie-percent">{summary.gabungan.persen}%</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">Hadir</span>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-3 mt-4 text-xs">
                  {[["Hadir", "hadir"], ["Izin", "izin"], ["Sakit", "sakit"], ["Alpa", "alpa"]].map(([l, k]) => (
                    <span key={k} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[k] }} />{l}: {summary.gabungan[k]}</span>
                  ))}
                </div>
                <div className="w-full mt-4 border-t border-white/50 pt-3" data-testid="wali-attendance-history">
                  <p className="text-xs font-semibold text-emerald-800 mb-2">Riwayat Kehadiran Terbaru</p>
                  {summary.absensi.length === 0 ? <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-2">Belum ada data kehadiran.</p> : (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                      {[...summary.absensi].sort((a, b) => (b.tanggal || "").localeCompare(a.tanggal || "")).slice(0, 8).map((a) => (
                        <div key={a.id} className="flex items-center justify-between text-xs bg-white/40 dark:bg-white/5 rounded-lg px-3 py-1.5">
                          <span className="text-slate-600 dark:text-slate-300">{a.tanggal} · <span className="text-slate-400 dark:text-slate-500">{a.nama_kegiatan}</span></span>
                          <StatusBadge status={a.status} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="lg:col-span-3 grid gap-6 sm:grid-cols-2">
                <RekapAbsensi title="Rekap Keaktifan Ngaji Dhuha" kategori="ngaji_dhuha" colorKey="ngaji" data={summary.absensi} tally={summary.ngaji_dhuha} testId="table-absensi-ngaji" />
                <RekapAbsensi title="Keaktifan Departemen" kategori="keaktifan_departemen" colorKey="departemen" data={summary.absensi} tally={summary.keaktifan_departemen} testId="table-departemen" />
                <RekapAbsensi title="Keaktifan Ribath" kategori="keaktifan_ribath" colorKey="ribath" data={summary.absensi} tally={summary.keaktifan_ribath} testId="table-ribath" className="sm:col-span-2" />
              </div>
            </div>

            {/* Violation history */}
            <div className="glass rounded-2xl p-5" data-testid="table-poin-pelanggaran">
              <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-4 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-rose-500" /> Riwayat Poin Pelanggaran</h3>
              {summary.pelanggaran.length === 0 ? (
                <p className="text-center text-slate-400 dark:text-slate-500 py-6 text-sm">Alhamdulillah, tidak ada catatan pelanggaran. 🌱</p>
              ) : (
                <div className="space-y-2">
                  {summary.pelanggaran.map((p) => (
                    <div key={p.id} className="flex items-start justify-between gap-3 bg-white/50 dark:bg-white/5 rounded-xl p-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap"><p className="font-medium text-emerald-950 dark:text-emerald-50">{p.jenis}</p><StatusBadge status={p.kategori} /></div>
                        <p className="text-xs text-slate-500 dark:text-slate-300 mt-0.5">{p.keterangan || "-"}{p.penanganan ? ` · Penanganan: ${p.penanganan}` : ""}</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{p.tanggal}</p>
                      </div>
                      <span className="text-sm font-bold text-rose-600 shrink-0">-{p.poin}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav items={waliBottomNav} />
    </div>
  );
}

function RekapAbsensi({ title, kategori, colorKey, data, tally, testId, className = "" }) {
  const rows = data.filter((a) => a.kategori === kategori).slice(0, 8);
  const c = CAT_COLORS[colorKey] || CAT_COLORS.ngaji;
  const hasData = tally.dari_sheet || tally.total > 0;
  return (
    <div className={`glass rounded-2xl p-5 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 flex items-center gap-2 min-w-0"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.solid }} /><span className="truncate">{title}</span></h3>
        <Badge className="border-0 shrink-0" style={{ background: `${c.solid}1f`, color: c.solid }}>{hasData ? `${tally.persen}% hadir` : "–"}</Badge>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden mb-4">
        <div className="h-full rounded-full" style={{ width: `${hasData ? tally.persen : 0}%`, background: `linear-gradient(90deg, ${c.grad[0]}, ${c.grad[1]})` }} />
      </div>
      {rows.length > 0 ? (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-sm bg-white/40 dark:bg-white/5 rounded-lg px-3 py-2">
              <div className="min-w-0"><p className="font-medium text-emerald-950 dark:text-emerald-50 truncate">{a.nama_kegiatan}</p><p className="text-xs text-slate-400 dark:text-slate-500">{a.tanggal}</p></div>
              <StatusBadge status={a.status} />
            </div>
          ))}
        </div>
      ) : hasData ? (
        <p className="text-xs text-slate-500 dark:text-slate-300 py-4 text-center leading-snug bg-white/30 dark:bg-white/5 rounded-lg px-3">Rincian harian tidak tersedia untuk kategori ini — data berasal dari rekap agregat bulanan Google Sheet.</p>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500 py-4 text-center">Belum ada data.</p>
      )}
    </div>
  );
}

function TrendDot({ cx, cy, payload, color }) {
  if (cx == null || cy == null) return null;
  const on = payload?.ongoing;
  return <circle cx={cx} cy={cy} r={on ? 5 : 3.2} fill={on ? "#ffffff" : color} stroke={color} strokeWidth={on ? 2 : 0} />;
}


function WaConsentBanner() {
  const { data, refetch } = useQuery({ queryKey: ["wa-consent"], queryFn: async () => (await api.get("/wali/wa-consent")).data });
  const [busy, setBusy] = useState(false);
  if (!data || data.no_wa || data.opt_in !== null) return null;
  const set = async (v) => {
    setBusy(true);
    try {
      await api.post("/wali/wa-consent", { opt_in: v });
      toast.success(v ? "Notifikasi rekap bulanan diaktifkan" : "Baik, kami tidak akan mengirim notifikasi");
      refetch();
    } catch { toast.error("Gagal menyimpan pilihan"); }
    finally { setBusy(false); }
  };
  return (
    <div className="glass rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 border border-emerald-200/60 dark:border-emerald-800/40" data-testid="wa-consent-banner">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="h-9 w-9 shrink-0 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center justify-center"><Info className="h-4 w-4" /></div>
        <p className="text-sm text-emerald-950 dark:text-emerald-50 leading-snug">Izinkan pondok mengirim <strong>Rekap Keaktifan Bulanan</strong> putra/putri Anda (PDF &amp; gambar) ke WhatsApp {data.masked_wa}? Dikirim 1× per bulan. Anda dapat berhenti kapan saja dengan membalas <strong>STOP</strong>.</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button size="sm" variant="outline" className="rounded-xl bg-white/60 dark:bg-white/5" data-testid="wa-consent-decline" disabled={busy} onClick={() => set(false)}>Nanti</Button>
        <Button size="sm" className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="wa-consent-accept" disabled={busy} onClick={() => set(true)}>Ya, kirimkan</Button>
      </div>
    </div>
  );
}
